import { useCallback, useEffect, useState } from "react";
import type { AgencyRelaunchPreviewViewV1 } from "@enrein/shared-contracts/contracts/relaunch-preview";
import type { StudioApiClient } from "./api.js";

export interface RelaunchPreviewPanelProps {
  readonly api: StudioApiClient;
  readonly claimId: string | null;
}

export function RelaunchPreviewPanel({ api, claimId }: RelaunchPreviewPanelProps) {
  const [preview, setPreview] = useState<AgencyRelaunchPreviewViewV1 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!claimId) {
      setPreview(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const row = await api.getRelaunchPreview(claimId);
      setPreview(row);
    } catch (caught) {
      setPreview(null);
      if (caught instanceof Error && !caught.message.includes("relaunch_preview_not_found")) {
        setError(caught.message);
      }
    } finally {
      setLoading(false);
    }
  }, [api, claimId]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async () => {
    if (!claimId) return;
    setError(null);
    setMessage(null);
    try {
      const row = await api.generateRelaunchPreview(claimId);
      setPreview(row);
      setMessage(row.idempotentReplay ? "Relaunch preview already generated." : "Relaunch preview generated (draft-only).");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  if (!claimId) {
    return <p className="muted">Select an opportunity to preview a proof relaunch path.</p>;
  }

  return (
    <div className="relaunch-preview-panel">
      <div className="panel-header">
        <h3>Proof Relaunch preview</h3>
        <button type="button" onClick={() => void generate()} disabled={loading}>
          Generate preview
        </button>
      </div>
      {message ? <p className="success">{message}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {loading && !preview ? <p>Loading relaunch preview…</p> : null}
      {preview?.status === "COMPLETE" ? (
        <>
          <dl className="detail-grid">
            <dt>Template</dt>
            <dd>{preview.templateKind}</dd>
            <dt>Project ID</dt>
            <dd>{preview.projectId}</dd>
            <dt>Draft ID</dt>
            <dd>{preview.draftId}</dd>
            <dt>Source score</dt>
            <dd>
              {preview.sourceScore} — {preview.sourceScoreLabel}
            </dd>
          </dl>
          <h4>Current issues</h4>
          <ul className="list">
            {preview.currentIssues?.map((issue) => (
              <li key={`${issue.title}-${issue.detail}`}>
                <strong>{issue.title}</strong> — {issue.detail}
              </li>
            ))}
          </ul>
          <h4>Proposed relaunch</h4>
          <ul className="list">
            {preview.proposedRelaunch?.map((row) => (
              <li key={`${row.title}-${row.improvementKind}`}>
                <strong>{row.title}</strong> — {row.detail}
              </li>
            ))}
          </ul>
          {preview.draftProposalNote ? <p className="muted">{preview.draftProposalNote}</p> : null}
          {preview.disclaimers?.map((line) => (
            <p key={line} className="muted">
              {line}
            </p>
          ))}
        </>
      ) : preview ? (
        <p className="muted">Relaunch preview status: {preview.status}</p>
      ) : (
        <p className="muted">No relaunch preview yet. Generate one from the selected opportunity.</p>
      )}
    </div>
  );
}
